import React, { useState, useEffect, useContext } from "react";
import { Joyride, STATUS, EVENTS, ACTIONS } from "react-joyride";
import Fab from "@material-ui/core/Fab";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import SchoolIcon from "@material-ui/icons/School";
import { useHistory, useLocation } from "react-router-dom";
import { AuthContext } from "../context/Auth/AuthContext";

var ACTIVE_KEY = "onboarding_usage_active";
var STEP_KEY = "onboarding_usage_step";

var STEPS = [
  {
    target: '[data-tour="tickets-manager"]',
    title: "Tela de Atendimento",
    content: "Esta é a tela principal. 'Aguardando': clientes esperando. 'Atendendo': com agente ativo. 'Resolvidos': encerrados.",
    disableBeacon: true,
    placement: "right",
    page: "/tickets",
  },
  {
    target: '[data-tour="tab-pending"]',
    title: "Aba Aguardando",
    content: "Mostra tickets aguardando atendimento. Clique em qualquer ticket para assumir e iniciar o atendimento.",
    disableBeacon: true,
    placement: "bottom",
    page: "/tickets",
  },
  {
    target: "body",
    title: "Abra um ticket para continuar",
    content: "Clique em um ticket da aba 'Aguardando' ou 'Atendendo' para continuar o tour dentro do atendimento.",
    disableBeacon: true,
    placement: "center",
    page: "/tickets",
  },
  {
    target: '[data-tour="message-input"]',
    title: "Caixa de Mensagem",
    content: "Digite sua mensagem e pressione Enter para enviar. Shift+Enter quebra linha. Digite '/' para acessar respostas rápidas.",
    disableBeacon: true,
    placement: "top",
  },
  {
    target: '[data-tour="emoji-button"]',
    title: "Enviar Emoji",
    content: "Abre o seletor de emojis. Clique no emoji desejado para inserir na mensagem antes de enviar.",
    disableBeacon: true,
    placement: "top",
  },
  {
    target: '[data-tour="attach-button"]',
    title: "Menu de Anexos",
    content: "Abre o menu: Fotos/Vídeos, Câmera, Documentos (PDF, Word, Excel), Contato, Enquete, Localização e PIX.",
    disableBeacon: true,
    placement: "top",
  },
  {
    target: '[data-tour="private-note-button"]',
    title: "Nota Privada",
    content: "Alterna para nota interna — visível apenas para sua equipe. O cliente nunca recebe essas mensagens.",
    disableBeacon: true,
    placement: "top",
  },
  {
    target: '[data-tour="pause-button"]',
    title: "Pausar Atendimento",
    content: "Ativa o modo pausa/almoço. O cliente recebe mensagem automática informando que você está indisponível.",
    disableBeacon: true,
    placement: "top",
  },
  {
    target: '[data-tour="transfer-button"]',
    title: "Transferir Ticket",
    content: "Transfere para outro agente ou setor. Use 'Retornar' para devolver à fila ou os três pontos (...) para mais opções.",
    disableBeacon: true,
    placement: "top",
  },
  {
    target: '[data-tour="resolve-button"]',
    title: "Encerrar Ticket",
    content: "Finaliza o atendimento. O ticket vai para 'Resolvidos' e o cliente recebe a mensagem de encerramento configurada.",
    disableBeacon: true,
    placement: "top",
  },
  {
    target: "body",
    title: "Tudo pronto!",
    content: "Você conheceu todas as funções principais. Explore o menu lateral: Contatos, Agendamentos, Relatórios, Configurações e muito mais. Bom trabalho!",
    disableBeacon: true,
    placement: "center",
  },
];

var useStyles = makeStyles(function(theme) {
  return {
    fab: {
      position: "fixed",
      bottom: theme.spacing(3),
      left: theme.spacing(3),
      zIndex: 1300,
    },
  };
});

function OnboardingUsageTour() {
  var classes = useStyles();
  var theme = useTheme();
  var history = useHistory();
  var location = useLocation();
  var auth = useContext(AuthContext);
  var user = auth.user;

  var isAdmin = user && (user.profile === "admin" || user.super === true);

  var activeState = useState(sessionStorage.getItem(ACTIVE_KEY) === "1");
  var active = activeState[0];
  var setActive = activeState[1];

  var stepState = useState(parseInt(sessionStorage.getItem(STEP_KEY) || "0", 10));
  var stepIndex = stepState[0];
  var setStepIndex = stepState[1];

  useEffect(function() {
    function onStart() {
      sessionStorage.setItem(ACTIVE_KEY, "1");
      sessionStorage.setItem(STEP_KEY, "0");
      setActive(true);
      setStepIndex(0);
      if (!location.pathname.startsWith("/tickets")) {
        history.push("/tickets");
      }
    }
    window.addEventListener("onboarding-usage-start", onStart);
    return function() { window.removeEventListener("onboarding-usage-start", onStart); };
  }, []);

  if (!isAdmin) return null;

  function startTour() {
    sessionStorage.setItem(ACTIVE_KEY, "1");
    sessionStorage.setItem(STEP_KEY, "0");
    setActive(true);
    setStepIndex(0);
    if (!location.pathname.startsWith("/tickets")) {
      history.push("/tickets");
    }
  }

  function endTour() {
    sessionStorage.removeItem(ACTIVE_KEY);
    sessionStorage.removeItem(STEP_KEY);
    setActive(false);
    setStepIndex(0);
  }

  function handleCallback(data) {
    var status = data.status;
    var type = data.type;
    var action = data.action;
    var index = data.index;

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      endTour();
      return;
    }

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      var nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      var nextStep = STEPS[nextIndex];
      if (nextStep && nextStep.page && !location.pathname.startsWith(nextStep.page)) {
        history.push(nextStep.page);
      }
      sessionStorage.setItem(STEP_KEY, String(nextIndex));
      setStepIndex(nextIndex);
    }
  }

  var joyrideStyles = {
    options: {
      primaryColor: theme.palette.primary.main,
      zIndex: 10000,
    },
    tooltip: {
      borderRadius: 10,
      fontSize: 14,
      maxWidth: 360,
    },
    tooltipTitle: {
      fontSize: 15,
      fontWeight: 700,
      marginBottom: 8,
    },
    tooltipContent: {
      lineHeight: 1.55,
    },
    buttonNext: {
      borderRadius: 6,
      fontSize: 13,
      fontWeight: 700,
      padding: "6px 18px",
    },
    buttonBack: {
      fontSize: 13,
      marginRight: 8,
    },
    buttonSkip: {
      fontSize: 12,
      color: "#999",
    },
    spotlight: {
      borderRadius: 6,
    },
  };

  var locale = {
    back: "← Voltar",
    close: "Fechar",
    last: "Concluir",
    next: "Próximo →",
    skip: "Pular tudo",
    open: "Abrir",
  };

  return React.createElement(
    React.Fragment,
    null,
    React.createElement(Joyride, {
      steps: STEPS,
      run: active,
      stepIndex: stepIndex,
      continuous: true,
      showSkipButton: true,
      showProgress: true,
      callback: handleCallback,
      styles: joyrideStyles,
      locale: locale,
      spotlightClicks: false,
      disableOverlayClose: true,
      scrollToFirstStep: true,
      scrollOffset: 80,
    }),
    !active && React.createElement(
      Tooltip,
      { title: "Tour interativo — como usar o sistema", placement: "right" },
      React.createElement(
        Fab,
        {
          color: "primary",
          className: classes.fab,
          onClick: startTour,
          size: "medium",
        },
        React.createElement(SchoolIcon, null)
      )
    )
  );
}

export default OnboardingUsageTour;
